import { HttpResourceOptions, HttpResourceRef, HttpResourceRequest, httpResource } from "@angular/common/http";
import { ResourceOptions, ResourceRef, Signal, computed, resource } from "@angular/core";

// Wrappers around `resource` and `httpResource` that add a `safeValue` signal.
// `value()` throws while a resource is in the error state; `safeValue()` returns
// the configured `defaultValue` instead, so callers can never trigger that throw.
// The full resource API (value, status, error, reload, ...) stays available.

export function safeResource<T, R>(
  options: ResourceOptions<T, R> & { defaultValue: NoInfer<T> },
): ResourceRef<T> & { safeValue: Signal<T> } {
  // eslint-disable-next-line nwd/no-raw-resource -- this is the sanctioned wrapper
  const ref = resource(options);
  return Object.assign(ref, {
    safeValue: computed(() => (ref.hasValue() ? ref.value() : options.defaultValue)),
  });
}

export function safeHttpResource<TResult>(
  urlOrRequest: (() => string | undefined) | (() => HttpResourceRequest | undefined),
  options: HttpResourceOptions<TResult, unknown> & { defaultValue: NoInfer<TResult> },
): HttpResourceRef<TResult> & { safeValue: Signal<TResult> } {
  // eslint-disable-next-line nwd/no-raw-resource -- this is the sanctioned wrapper
  const ref = httpResource<TResult>(urlOrRequest as () => string | undefined, options);
  return Object.assign(ref, {
    safeValue: computed(() => (ref.hasValue() ? ref.value() : options.defaultValue)),
  });
}
